import deburr from 'lodash/deburr'
import uniq from 'lodash/uniq'

import { channelsInstance } from '@/api/instances'
import { CHANNEL_CATEGORIES, MessageTypes } from '@/models/channels'
import { DocumentTypes } from '@/models/documentTypes'
import { configTypes } from '@/models/featureConfig'
import {
  arrayRemove,
  arrayUnion,
  deleteField,
  serverTimestamp
} from '@/modules/auth/index-firebase'
import { shouldShowAllMessages } from '@/utils/channel'
import DatabaseFactory from '@/wrappers/database'
import { addErrorLog } from '@/wrappers/database/utils/addLog'
import Logger, {
  EventModel,
  EventName,
  LogLevel,
  ProviderType
} from '@/wrappers/logger'

import { getMessagesContext } from './utils'

const fetchMacroByQuery = async (store, { query }) => {
  try {
    const responseBody = await channelsInstance.get(
      `channels/macros?shortcut=${query}`
    )

    const mappedMacros = responseBody.map(macro => {
      return {
        key: macro.shortcut,
        value: macro.message
      }
    })

    store.commit('setMacros', mappedMacros)
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'pillars/channel/message/fetchMacros' },
      { root: true }
    )
  }
}

const buildFetchMessagesFilters = async (store, channel) => {
  const redirectAssistToAdminLimitDate = await store.dispatch(
    'featureConfig/getFeatureFlagById',
    {
      id: 'ehr-enable_redirect_assistance_admin_date',
      type: configTypes.STRING
    },
    { root: true }
  )
  const staffCanViewAllMessages =
    store.rootState.permissions.channels.staff.viewAllChannelMessages

  const showAllMessages = shouldShowAllMessages({
    channel: channel,
    featureFlagValue: redirectAssistToAdminLimitDate,
    staffCanViewAllMessages
  })

  return showAllMessages
    ? []
    : [
        {
          field: 'channelCategory',
          condition: '==',
          value: CHANNEL_CATEGORIES.ADMINISTRATIVE
        }
      ]
}

const fetchMessages = async (store, { paginating = true, signal, channel }) => {
  const MESSAGES_PER_PAGE = 50
  store.commit('clearMessagesListener')
  store.commit('incrementCurrentPage')

  const isPrivate = store.state.isPrivate
  const collection = isPrivate ? 'privateMessages' : 'messages'

  const filters = await buildFetchMessagesFilters(store, channel)
  const limit = store.state.currentPage * MESSAGES_PER_PAGE

  const query = {
    filters,
    orderBy: [{ field: 'createdAt', sort: 'desc' }]
  }

  if (paginating) {
    query.limit = limit
  }
  const database = new DatabaseFactory(
    `channels/${channel.id}/${collection}`
  ).getReadDatabase()

  if (signal?.aborted) return

  const listener = await database.getStream(
    query,
    snapshot => {
      const docs = snapshot.docs.reverse()

      if (signal?.aborted) return

      if (
        store.rootState.pillars.personId &&
        channel.id !== store.rootState.pillars.channel.currentChannel.id
      ) {
        Logger.addLog(
          EventName.INTERACTION_ERROR,
          {
            model: EventModel.CHANNELS,
            modelId: channel.id,
            context: 'mixed_data_messages',
            data: {
              channelId: store.rootState.pillars.channel.currentChannel.id,
              snapshotChannelId: channel.id,
              pillarsPersonId: store.rootState.pillars.personId
            }
          },
          {
            isAction: true,
            logLevel: LogLevel.ERROR
          },
          [ProviderType.DATADOG]
        )

        return
      }

      store.commit('setFormattedCurrentMessages', {
        isPrivate,
        ...store.rootGetters['pillars/channel/channelMessagesMutationData'](
          isPrivate
        ),
        docs: docs
      })

      const endPagination = docs.length < limit
      if (endPagination || !paginating) {
        store.commit('setEndPagination', true)
      }
    },
    error =>
      addErrorLog({
        context: 'message/actions - fetchMessages',
        channelId: channel.id,
        error
      })
  )

  store.commit('setMessagesListener', listener)
}

const setFilteredMessages = (store, search) => {
  store.commit('clearHighlight')
  const messages =
    store.state.formattedCurrentMessages[
      getMessagesContext(store.state.isPrivate)
    ]

  const searchLower = search.toLowerCase()

  const filtered = messages
    .filter(message =>
      [MessageTypes.TEXT, MessageTypes.ANSWER, MessageTypes.QUESTION].includes(
        message.type
      )
    )
    .filter(message => {
      return deburr(message.content.toLowerCase()).includes(deburr(searchLower))
    })

  store.commit('setFilteredMessages', filtered)
}

const sendMessage = async (
  store,
  { message, type, mentionStaffIds, channel }
) => {
  const channelId = channel.id
  const currentUserId = store.rootState.user.user?.id

  if (!channelId || !currentUserId) {
    return
  }
  type = type || MessageTypes.TEXT

  const messageToCreate = buildMessage(
    currentUserId,
    message,
    type,
    mentionStaffIds,
    store.state.replyTo
  )

  const isPrivate = store.state.isPrivate
  const collectionMessages = isPrivate ? 'privateMessages' : 'messages'
  try {
    const database = new DatabaseFactory(
      `channels/${channelId}/${collectionMessages}`
    ).getWriteDatabase()

    await database.create(messageToCreate)

    store.commit('clearReplyTo')
  } catch (error) {
    addErrorLog({
      context: 'message/actions - sendMessage',
      channelId,
      error
    })
  }

  store.dispatch('deleteStarred', channelId)
}

const deleteStarred = async (store, channelId) => {
  const database = new DatabaseFactory(
    `channels/${channelId}`
  ).getWriteDatabase()
  try {
    await database.update({ starred: deleteField() })
  } catch (error) {
    addErrorLog({
      context: 'message/actions - sendMessage - starred',
      channelId,
      error
    })
  }
}

const sendFile = async (store, { file, channel }) => {
  if (!file) {
    return
  }
  const validationMessage = validateFile(file)
  if (validationMessage) {
    store.dispatch(
      'errors/setError',
      { message: validationMessage },
      { root: true }
    )
    return
  }
  store.commit('setSendingFile', true)

  const members = store.rootState.member.all
  const memberId = members[channel.personId].id
  try {
    const fileResponse = await store.dispatch(
      'file/uploadFile',
      {
        file,
        personId: memberId
      },
      { root: true }
    )

    if (fileResponse) {
      store.dispatch('sendMessage', {
        message: fileResponse.url,
        type: DocumentTypes[fileResponse.type.toUpperCase()],
        channel
      })
    }
  } catch (error) {
    store.dispatch('errors/setError', { error }, { root: true })
  } finally {
    store.commit('setSendingFile', false)
  }
}

const validateFile = file => {
  const fileSize = file.size / 1024 / 1024

  if (fileSize > 10) {
    return 'Não é possível adicionar um arquivo maior do que 10 mb!'
  }

  const nameArray = file.name.split('.')
  const fileExtension = nameArray[nameArray.length - 1]
  const documentType = DocumentTypes[fileExtension?.toUpperCase()]
  if (!documentType) {
    return 'Não é possível adicionar arquivo do tipo ' + file.type
  }

  return ''
}

const updateChannel = async (store, { withLastSync, channel }) => {
  const channelId = channel.id
  const currentUserId = store.rootState.user.user?.id
  const lastSync = withLastSync ? getLastSync(currentUserId) : {}
  const resetTyping = getResetTyping(currentUserId)

  const database = new DatabaseFactory(
    `channels/${channelId}`
  ).getWriteDatabase()
  try {
    await database.update({
      ...resetTyping,
      ...lastSync
    })
  } catch (error) {
    addErrorLog({
      context: 'message/actions - updateChannel',
      channelId,
      error
    })
  }
}

const updateTyping = async (store, { typing, channel }) => {
  const isPrivate = store.state.isPrivate
  const field = isPrivate ? 'privateTyping' : 'typing'
  const currentUserId = store.rootState.user.user?.id
  const channelId = channel.id

  const updateTyping = {}
  updateTyping[`${field}`] = typing
    ? arrayUnion(currentUserId)
    : arrayRemove(currentUserId)

  const database = new DatabaseFactory(
    `channels/${channelId}`
  ).getWriteDatabase()
  try {
    await database.update(updateTyping)
  } catch (error) {
    addErrorLog({
      context: 'message/actions - updateTyping',
      channelId,
      error
    })
  }
}

const getLastSync = currentUserId => {
  const update = {}
  update[`staff.${currentUserId}.lastSync`] = serverTimestamp()

  update[`staff.${currentUserId}.unreadMessages`] = 0
  update[`staff.${currentUserId}.privateUnreadMessages`] = 0
  update[`staff.${currentUserId}.privateUnreadMentions`] = 0

  return update
}

const getResetTyping = currentUserId => {
  const update = {}
  update['typing'] = arrayRemove(currentUserId)
  update['privateTyping'] = arrayRemove(currentUserId)
  return update
}

const setReplyTo = (store, message) => {
  store.commit('setReplyTo', message)
}

const clearReplyTo = store => {
  store.commit('clearReplyTo')
}

const deleteMessage = async (_, { channelId, messageId }) => {
  return await channelsInstance.delete(
    `channels/${channelId}/message/${messageId}`
  )
}

const clearMessages = store => {
  store.commit('clearMessagesListener')
  store.commit('resetFormattedCurrentMessages')
  store.commit('clearReplyTo')
  store.commit('setIsPrivate', false)
  store.dispatch('resetPagination')
}

const clearMessagesListener = store => {
  store.commit('clearMessagesListener')
}

const getReactionIcons = async store => {
  const reactions = await store.dispatch(
    'featureConfig/getFeatureFlagById',
    {
      id: 'channels-allowed_reactions'
    },
    { root: true }
  )
  return reactions
}

const toggleReaction = async (store, { messageId, icon, reactions }) => {
  const channelId = store.rootState.pillars.channel.currentChannel?.id
  const currentUserId = store.rootState.user.user?.id
  if (!isReactionValid(store, channelId, currentUserId)) {
    return 'not valid'
  }

  const isPrivate = store.state.isPrivate
  const collectionMessages = isPrivate ? 'privateMessages' : 'messages'
  const database = new DatabaseFactory(
    `channels/${channelId}/${collectionMessages}/${messageId}`
  ).getWriteDatabase()
  try {
    const reactionExisted = getReactionClicked(reactions, currentUserId)
    if (reactionExisted) {
      await database.update({
        reactions: arrayRemove(reactionExisted)
      })
      if (reactionExisted.icon === icon) {
        return 'reaction removed'
      }
    }

    const newReaction = {
      icon,
      userId: currentUserId,
      createdAt: new Date()
    }
    await database.update({
      reactions: arrayUnion(newReaction)
    })
    return 'reaction added'
  } catch (error) {
    store.dispatch(
      'errors/setError',
      {
        message: 'Não foi possível inserir a reação, tente novamente'
      },
      { root: true }
    )
    addErrorLog({
      context: 'message/actions - sendReaction',
      channelId,
      error
    })
  }
}

const getReactionClicked = (reactions, currentUserId) => {
  return reactions?.find(reaction => reaction.userId === currentUserId)
}

const isReactionValid = (store, channelId, userId) => {
  if (!channelId || !userId) {
    store.dispatch(
      'errors/setError',
      {
        message: 'Não foi possível inserir a reação'
      },
      { root: true }
    )
    return false
  }
  return true
}

const buildMessage = (
  currentUserId,
  message,
  type,
  mentionStaffIds,
  replyTo
) => {
  const currentDate = serverTimestamp()
  const messageToCreate = {
    aliceId: currentUserId,
    userId: currentUserId,
    content: message,
    createdAt: currentDate,
    type: type,
    mentionStaffIds: mentionStaffIds?.length ? uniq(mentionStaffIds) : null
  }

  if (replyTo) {
    messageToCreate.replyTo = {
      messageId: replyTo.id,
      content: replyTo.content,
      type: replyTo.type,
      userId: replyTo.userId,
      createdAt: replyTo.createdAt
    }
  }
  return messageToCreate
}

const setIsPrivate = (store, isPrivate) => {
  store.commit('setIsPrivate', isPrivate)
  store.dispatch('resetPagination')
}
const resetPagination = store => {
  store.commit('setEndPagination', false)
  store.commit('resetCurrentPage')
}

export default {
  fetchMessages,
  fetchMacroByQuery,
  setFilteredMessages,
  sendMessage,
  sendFile,
  updateChannel,
  updateTyping,
  deleteMessage,
  clearMessages,
  clearMessagesListener,
  deleteStarred,
  getReactionIcons,
  toggleReaction,
  setReplyTo,
  clearReplyTo,
  setIsPrivate,
  resetPagination
}
